import { useState, useEffect, useRef } from "react";
import { Row, Col, Button, Form, Card, Dropdown, Modal } from "react-bootstrap";
import { FiDownload, FiCalendar, FiChevronDown, FiLayout, FiTarget, FiXCircle } from "react-icons/fi";
import CommonDropdown from "../common/common_dropDown";
import DateRangePicker from "../common/dateRange";
import { transportReportApi, saveTemplateApi, getTemplatesApi, getTemplateByIdApi, downloadBase64File, getquickreportApi, exportquickApi } from "./report-api";
import LoaderModal from "../common/loader";
import { format } from "date-fns";
import CommonSnackbar from "../common/reusable-commponent";
import { sanitizeInput, sanitizeNumericInput } from "../common/security-utils";
import { useGlobal } from "../../GlobalContext";

// Assets
import report from "../../assets/svg/report.svg";
import che_lis from "../../assets/svg/che_lis.svg";
import coin from "../../assets/svg/coin.svg";
import company from "../../assets/svg/company.svg";
import dept from "../../assets/svg/dept.svg";
import drive from "../../assets/svg/drive.svg";
import gate from "../../assets/svg/gate.svg";
import lov_rep from "../../assets/svg/loc_rep.svg";
import meeting from "../../assets/svg/meeting.svg";
import monthly from "../../assets/svg/monthly.svg";
import org from "../../assets/svg/org.svg";
import pop from "../../assets/svg/pop.svg";
import reg from "../../assets/svg/reg.svg";
import time from "../../assets/svg/time.svg";

const Report = () => {
  const { rolePermissionList } = useGlobal();
  const apiUrl = process.env.REACT_APP_API_URL;

  const [activeTab, setActiveTab] = useState("Customized");
  const [loading, setLoading] = useState(false);

  // Filter States
  const [type, setType] = useState({ label: "Trip", value: "Trip" });
  const [customer, setCustomer] = useState(null);
  const [product, setProduct] = useState(null);
  const [vehicle, setVehicle] = useState(null);
  const [driver, setDriver] = useState(null);
  const [address, setAddress] = useState(null);
  const [status, setStatus] = useState(null);
  const [milestone, setMilestone] = useState(null);
  const [stop, setStop] = useState(null);
  const [customizedDateRange, setCustomizedDateRange] = useState({ start: null, end: null });
  const [quickDateRange, setQuickDateRange] = useState({ start: null, end: null });

  // Template States
  const [templates, setTemplates] = useState([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [templateName, setTemplateName] = useState("");

  // Snackbar Context for secure notifications
  const [snackbar, setSnackbar] = useState({ show: false, message: "", isSuccess: true });

  const showSnackbar = (message, isSuccess = true) => {
    setSnackbar({ show: true, message, isSuccess });
  };

  /**
   * Universal sanitizer helper
   * Handles both strings and numeric IDs to ensure VAPT compliance without breaking data types
   */
  const safeSanitize = (val) => {
    if (val === null || val === undefined) return "";
    return sanitizeInput(String(val));
  };

  /**
   * VAPT Compliance: Permission check hardened against prototype pollution
   */
  const checkPermission = (permission) => {
    if (!rolePermissionList || !Array.isArray(rolePermissionList)) return false;
    return rolePermissionList.some(
      (element) => element?.name === "Report" && element?.menu_permission === true && element?.[permission] === true
    );
  };

  const [quickReportList, setQuickReportList] = useState([]);
  const [loadingReportId, setLoadingReportId] = useState(null);

  const isLoadingTemplate = useRef(false);

  useEffect(() => {
    if (checkPermission("view_permission")) {
      fetchTemplates();
      getQuickReport();
    }
  }, [rolePermissionList]);

  useEffect(() => {
    if (type?.value && !isLoadingTemplate.current) {
      handleReset(false); // Reset but keep the type
    }
  }, [type?.value]);

  const fetchTemplates = async () => {
    try {
      const response = await getTemplatesApi();
      if (response?.data?.success) {
        setTemplates(response.data.suggestionlist || []);
      }
    } catch (err) {
      // Errors handled by api-service interceptor
    }
  };

  const handleTemplateSelect = async (templateId) => {
    const sanitizedId = safeSanitize(templateId);
    if (!sanitizedId) return;

    setLoading(true);
    isLoadingTemplate.current = true;
    try {
      const response = await getTemplateByIdApi(sanitizedId);
      if (response?.data?.success) {
        const data = response.data.template;
        setType(data.type ? { label: data.type, value: data.type } : null);
        setCustomer(data.customers?.[0] || null);
        setProduct(data.products?.[0] || null);
        setVehicle(data.vehicles?.[0] || null);
        setDriver(data.drivers?.[0] || null);
        setAddress(data.locations?.[0] || null);
        setStatus(data.status?.[0] || null);
        setMilestone(data.milestones?.[0] || null);
        setStop(data.stops?.[0] || null);

        if (data.from_date) {
          setCustomizedDateRange({
            start: new Date(data.from_date),
            end: data.to_date ? new Date(data.to_date) : null,
          });
        }
      }
    } catch (error) {
      showSnackbar("Failed to load template details.", false);
    } finally {
      setLoading(false);
      setTimeout(() => {
        isLoadingTemplate.current = false;
      }, 100);
    }
  };

  const handleReset = (fullReset = true) => {
    if (fullReset) setType(null);
    setCustomer(null);
    setProduct(null);
    setVehicle(null);
    setDriver(null);
    setAddress(null);
    setStatus(null);
    setMilestone(null);
    setStop(null);
    setCustomizedDateRange({ start: null, end: null });
  };

  const handleExport = async () => {
    if (!checkPermission("view_permission")) {
      showSnackbar("You do not have permission to export reports.", false);
      return;
    }

    setLoading(true);
    try {
      const payload = {
        type: safeSanitize(type?.value),
        customers: customer ? [safeSanitize(customer.value)] : [],
        products: product ? [safeSanitize(product.value)] : [],
        vehicles: vehicle ? [safeSanitize(vehicle.value)] : [],
        drivers: driver ? [safeSanitize(driver.value)] : [],
        locations: address ? [safeSanitize(address.value)] : [],
        status: status ? [safeSanitize(status.value)] : [],
        milestones: milestone ? [safeSanitize(milestone.value)] : [],
        stops: stop ? [safeSanitize(stop.value)] : [],
        from_date: customizedDateRange.start ? format(customizedDateRange.start, "yyyy-MM-dd") : null,
        to_date: customizedDateRange.end ? format(customizedDateRange.end, "yyyy-MM-dd") : null,
      };

      const response = await transportReportApi(payload);

      if (response?.data?.success) {
        if (response.data.base64) {
          downloadBase64File(response.data.base64, response.data.file_name || "report.xlsx");
          showSnackbar("Report downloaded successfully!");
        } else {
          showSnackbar(response.data.message || "Report generated.", true);
        }
      } else {
        showSnackbar(response?.data?.message || "Failed to generate report", false);
      }
    } catch (error) {
      showSnackbar("An error occurred while generating report.", false);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTemplate = async () => {
    if (!checkPermission("create_permission")) {
      showSnackbar("You do not have permission to save templates.", false);
      return;
    }

    const safeName = sanitizeInput(templateName);
    if (!safeName) {
      showSnackbar("Please enter a valid template name.", false);
      return;
    }
    setLoading(true);
    try {
      const payload = {
        template_name: safeName,
        type: safeSanitize(type?.value),
        customers: customer ? [safeSanitize(customer.value)] : [],
        products: product ? [safeSanitize(product.value)] : [],
        vehicles: vehicle ? [safeSanitize(vehicle.value)] : [],
        drivers: driver ? [safeSanitize(driver.value)] : [],
        locations: address ? [safeSanitize(address.value)] : [],
        status: status ? [safeSanitize(status.value)] : [],
        milestones: milestone ? [safeSanitize(milestone.value)] : [],
        stops: stop ? [safeSanitize(stop.value)] : [],
        from_date: customizedDateRange.start ? format(customizedDateRange.start, "yyyy-MM-dd") : null,
        to_date: customizedDateRange.end ? format(customizedDateRange.end, "yyyy-MM-dd") : null,
      };
      const response = await saveTemplateApi(payload);
      if (response?.data?.status || response?.data?.success) {
        showSnackbar("Template saved successfully");
        setShowSaveModal(false);
        setTemplateName("");
        fetchTemplates();
      } else {
        showSnackbar(response?.data?.message || "Failed to save template", false);
      }
    } catch (error) {
      showSnackbar("An error occurred while saving template.", false);
    } finally {
      setLoading(false);
    }
  };

  const getQuickReport = async () => {
    try {
      const response = await getquickreportApi();
      if (response?.success) {
        setQuickReportList(response.suggestionlist || []);
      }
    } catch (error) {
      // Handle silently as it's secondary data
    }
  };

  const exportQuickGet = async (option) => {
    if (!checkPermission("view_permission")) {
      showSnackbar("You do not have permission to export reports.", false);
      return;
    }

    const sanitizedValue = safeSanitize(option.value);
    if (!sanitizedValue) return;

    setLoadingReportId(option.value);
    try {
      const fromDate = quickDateRange.start ? format(quickDateRange.start, "yyyy-MM-dd") : null;
      const toDate = quickDateRange.end ? format(quickDateRange.end, "yyyy-MM-dd") : null;

      const response = await exportquickApi(sanitizedValue, fromDate, toDate);

      if (response?.data?.success) {
        if (response.data.base64) {
          downloadBase64File(response.data.base64, response.data.file_name || "report.xlsx");
          showSnackbar("Report downloaded successfully!");
        } else {
          showSnackbar("Report generated successfully!");
        }
      } else {
        showSnackbar(response?.data?.message || "Failed to export report", false);
      }
    } catch (error) {
      showSnackbar("An error occurred during quick export.", false);
    } finally {
      setLoadingReportId(null);
    }
  };

  const getIconForReport = (label) => {
    const lowerLabel = String(label).toLowerCase();
    let iconSrc = reg;
    if (lowerLabel.includes("monthly") || lowerLabel.includes("weekly")) iconSrc = monthly;
    else if (lowerLabel.includes("visit") || lowerLabel.includes("courier")) iconSrc = coin;
    else if (lowerLabel.includes("visitor") || lowerLabel.includes("meeting")) iconSrc = meeting;
    else if (lowerLabel.includes("gate")) iconSrc = gate;
    else if (lowerLabel.includes("location")) iconSrc = lov_rep;
    else if (lowerLabel.includes("department")) iconSrc = dept;
    else if (lowerLabel.includes("organization")) iconSrc = company;
    else if (lowerLabel.includes("checklist")) iconSrc = che_lis;
    else if (lowerLabel.includes("rejected")) iconSrc = org;
    else if (lowerLabel.includes("time")) iconSrc = time;
    else if (lowerLabel.includes("vehicle")) iconSrc = pop;
    else if (lowerLabel.includes("driver")) iconSrc = drive;

    return <img src={iconSrc} alt={label} style={{ width: "20px", height: "20px" }} />;
  };

  const renderFilters = () => {
    const typeValue = type?.value?.toLowerCase();
    if (typeValue === "trip") {
      return (
        <>
          <Col md={3}>
            <Form.Group>
              <Form.Label className="fw-bold fs-13 mb-2" style={{ color: "#28303F" }}>Vendor</Form.Label>
              <CommonDropdown apiUrl={`${apiUrl}vendor/autosuggestion`} placeholder="Select" value={customer} onSelect={setCustomer} searchView={true} />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label className="fw-bold fs-13 mb-2" style={{ color: "#28303F" }}>Vehicle</Form.Label>
              <CommonDropdown apiUrl={`${apiUrl}vehicle/autosuggestion`} placeholder="Select" value={vehicle} onSelect={setVehicle} searchView={true} />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label className="fw-bold fs-13 mb-2" style={{ color: "#28303F" }}>Milestone</Form.Label>
              <CommonDropdown apiUrl={`${apiUrl}milestone/autosuggestion`} placeholder="Select" value={milestone} onSelect={setMilestone} searchView={true} />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label className="fw-bold fs-13 mb-2" style={{ color: "#28303F" }}>Status</Form.Label>
              <CommonDropdown apiUrl={`${apiUrl}trips/status`} placeholder="Select" value={status} onSelect={setStatus} searchView={true} />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label className="fw-bold fs-13 mb-2" style={{ color: "#28303F" }}>Date Range</Form.Label>
              <DateRangePicker startDate={customizedDateRange.start} endDate={customizedDateRange.end} onChange={setCustomizedDateRange} style={{ height: "38px", border: "1px solid #ccc" }} />
            </Form.Group>
          </Col>
        </>
      );
    }
    if (typeValue === "stop") {
      return (
        <>
          <Col md={3}>
            <Form.Group>
              <Form.Label className="fw-bold fs-13 mb-2" style={{ color: "#28303F" }}>Customer</Form.Label>
              <CommonDropdown apiUrl={`${apiUrl}customer/autosuggestion`} placeholder="Select" value={customer} onSelect={setCustomer} searchView={true} />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label className="fw-bold fs-13 mb-2" style={{ color: "#28303F" }}>Stops</Form.Label>
              <CommonDropdown apiUrl={`${apiUrl}stops/autosuggestion`} placeholder="Select" value={stop} onSelect={setStop} searchView={true} />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label className="fw-bold fs-13 mb-2" style={{ color: "#28303F" }}>Product</Form.Label>
              <CommonDropdown apiUrl={`${apiUrl}item/autosuggestion`} placeholder="Select" value={product} onSelect={setProduct} searchView={true} />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label className="fw-bold fs-13 mb-2" style={{ color: "#28303F" }}>Address</Form.Label>
              <CommonDropdown apiUrl={`${apiUrl}location`} placeholder="Select" value={address} onSelect={setAddress} searchView={true} />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label className="fw-bold fs-13 mb-2" style={{ color: "#28303F" }}>Status</Form.Label>
              <CommonDropdown apiUrl={`${apiUrl}trips/status`} placeholder="Select" value={status} onSelect={setStatus} searchView={true} />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label className="fw-bold fs-13 mb-2" style={{ color: "#28303F" }}>Date Range</Form.Label>
              <DateRangePicker startDate={customizedDateRange.start} endDate={customizedDateRange.end} onChange={setCustomizedDateRange} style={{ height: "38px", border: "1px solid #ccc" }} />
            </Form.Group>
          </Col>
        </>
      );
    }
    return null;
  };

  if (!checkPermission("view_permission")) {
    return (
      <div className="d-flex align-items-center justify-content-center vh-100">
        <div className="text-center">
          <FiTarget size={48} className="text-muted mb-3" />
          <h4 className="fw-bold">Access Denied</h4>
          <p className="text-muted">You do not have permission to view reports.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-2 text-start" style={{ backgroundColor: "#f8f9fa", minHeight: "fit-content", fontFamily: "'Inter', sans-serif" }}>
      <LoaderModal show={loading} />

      <CommonSnackbar
        show={snackbar.show}
        message={snackbar.message}
        isSuccess={snackbar.isSuccess}
        onClose={() => setSnackbar({ ...snackbar, show: false })}
      />

      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="bg-white p-1 rounded-pill shadow-sm d-inline-flex">
          <Button
            variant={activeTab === "Customized" ? "primary" : "light"}
            className={`rounded-pill fw-500 px-4 ${activeTab === "Customized" ? "" : "bg-white text-muted border-0"}`}
            style={{ backgroundColor: activeTab === "Customized" ? "#3469E7" : "transparent", borderColor: activeTab === "Customized" ? "#3469E7" : "transparent", fontSize: "14px", paddingTop: "8px", paddingBottom: "8px" }}
            onClick={() => setActiveTab("Customized")}
          >
            Customized
          </Button>
          <Button
            variant={activeTab === "Quick Report" ? "primary" : "light"}
            className={`rounded-pill fw-500 px-4 ${activeTab === "Quick Report" ? "" : "bg-white text-muted border-0"}`}
            style={{ backgroundColor: activeTab === "Quick Report" ? "#3469E7" : "transparent", borderColor: activeTab === "Quick Report" ? "#3469E7" : "transparent", fontSize: "14px", paddingTop: "8px", paddingBottom: "8px" }}
            onClick={() => setActiveTab("Quick Report")}
          >
            Quick Report
          </Button>
        </div>

        <div className="d-flex gap-3">
          <Dropdown>
            <Dropdown.Toggle variant="white" className="bg-white border rounded-pill px-3 py-2 d-flex align-items-center gap-2 text-primary fw-500 fs-14 shadow-sm">
              <FiLayout size={16} /> My templates <FiChevronDown size={16} />
            </Dropdown.Toggle>
            <Dropdown.Menu style={{ maxHeight: "300px", overflowY: "auto" }}>
              {templates.length > 0 ? templates.map((t) => (
                <Dropdown.Item key={t.value} onClick={() => handleTemplateSelect(t.value)}>{t.label}</Dropdown.Item>
              )) : (
                <Dropdown.Item disabled>No templates found</Dropdown.Item>
              )}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      <Card className="border-0 shadow-sm rounded-4 overflow-hidden" style={{ minHeight: "600px" }}>
        <Row className="g-0 h-100">
          <Col md={3} className="p-4" style={{ backgroundColor: "#F8F9FE", borderRight: "1px solid #eee" }}>
            <div className="h-100 d-flex flex-column">
              <h5 className="fw-bold mb-3" style={{ color: "#1a1a1a" }}>Generate report</h5>
              <p className="text-muted fs-14 lh-base mb-5">Generate report with your specified filters. So you can see and manage that specified data which you want.</p>
              <div className="mt-auto d-flex justify-content-center">
                <img src={report} alt="Report Illustration" className="img-fluid" />
              </div>
            </div>
          </Col>

          <Col md={9} className="p-5 bg-white">
            {activeTab === "Customized" ? (
              <Form>
                <Row className="g-4 mb-4">
                  <Col md={3}>
                    <div className="fw-bold fs-13 mb-2" style={{ color: "#28303F" }}>Type</div>
                    <CommonDropdown apiUrl={`${apiUrl}get_report_type`} placeholder="Select Type" value={type} onSelect={setType} searchView={true} />
                  </Col>
                  {type && renderFilters()}
                </Row>

                {!type && (
                  <div className="text-center py-5">
                    <div className="text-muted fs-14">
                      <FiTarget size={48} className="mb-3" style={{ opacity: 0.3 }} />
                      <p className="fw-500">Please select a type to generate customized report</p>
                      <p className="fs-13">Choose from Stop, Trip, or other available report types</p>
                    </div>
                  </div>
                )}

                <div className="d-flex justify-content-between align-items-center mt-5 pt-5">
                  <Button variant="outline-none" className="rounded-pill px-4 py-2 fw-500 fs-14 d-flex align-items-center gap-2" style={{ borderColor: "#3469E7", color: "#3469E7" }} onClick={() => setShowSaveModal(true)}>
                    <FiDownload size={16} /> Save as template
                  </Button>

                  <div className="d-flex gap-3">
                    <Button variant="white" className="rounded-pill px-4 py-2 fw-500 fs-14 border" style={{ color: "#28303F" }} onClick={() => handleReset(true)}>Reset</Button>
                    <Button variant="primary" className="rounded-pill px-5 py-2 fw-500 fs-14" style={{ backgroundColor: "#3469E7", borderColor: "#3469E7" }} onClick={handleExport} disabled={!type}>Export</Button>
                  </div>
                </div>
              </Form>
            ) : (
              <div className="fade-in">
                <div className="text-center mb-4 position-relative">
                  <div style={{ borderTop: "1px dashed #e0e0e0", position: "absolute", top: "50%", width: "100%", zIndex: 0 }}></div>
                  <span className="bg-white px-3 position-relative fw-bold" style={{ zIndex: 1, color: "#28303F" }}>Just a single click is enough!</span>
                </div>

                <Row className="mb-4">
                  <Col className="d-flex justify-content-end align-items-center gap-2">
                    <div className="d-flex align-items-center px-2 py-1 bg-white border rounded-3">
                      <FiCalendar size={16} className="text-muted me-2" />
                      <DateRangePicker startDate={quickDateRange.start} endDate={quickDateRange.end} onChange={setQuickDateRange} style={{ border: "none" }} />
                    </div>
                    {(quickDateRange.start || quickDateRange.end) && (
                      <Button variant="outline-danger" size="sm" className="rounded-3" onClick={() => setQuickDateRange({ start: null, end: null })}>
                        <FiXCircle size={18} />
                      </Button>
                    )}
                  </Col>
                </Row>

                <Row className="g-3">
                  {quickReportList.map((reportItem, index) => (
                    <Col md={3} key={index}>
                      <div
                        className={`p-2 rounded-3 d-flex border align-items-center gap-3 shadow-sm h-100 ${loadingReportId === reportItem.value ? "opacity-50" : ""}`}
                        style={{ cursor: loadingReportId ? "not-allowed" : "pointer", backgroundColor: "#F8F8F8", transition: "all 0.2s" }}
                        onClick={() => !loadingReportId && exportQuickGet(reportItem)}
                      >
                        {loadingReportId === reportItem.value ? (
                          <div className="spinner-border spinner-border-sm text-primary mx-auto" />
                        ) : (
                          <>
                            {getIconForReport(reportItem.label)}
                            <span className="fw-500 fs-13 text-dark text-truncate">{reportItem.label}</span>
                          </>
                        )}
                      </div>
                    </Col>
                  ))}
                  {quickReportList.length === 0 && <Col className="text-center py-4 text-muted">No quick reports available.</Col>}
                </Row>
              </div>
            )}
          </Col>
        </Row>
      </Card>

      <Modal show={showSaveModal} onHide={() => setShowSaveModal(false)} centered>
        <Modal.Header closeButton className="border-0">
          <Modal.Title className="fs-18">Save Template</Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-4">
          <Form.Group>
            <Form.Label className="fs-14 fw-500">Template Name</Form.Label>
            <Form.Control
              type="text"
              className="rounded-3"
              placeholder="e.g. Monthly Trip Report"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              autoFocus
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="light" className="rounded-pill px-4" onClick={() => setShowSaveModal(false)}>Cancel</Button>
          <Button variant="primary" className="rounded-pill px-4" style={{ backgroundColor: "#3469E7" }} onClick={handleSaveTemplate}>Save Template</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Report;
